import $ from "jquery";
import {Rover} from "./Rover";
import {RoverUI} from "./RoverUI";
import {gsap} from "gsap/all";
import QRCode from "qrcodejs2";


export class Game {

  constructor(app) {
    this.app = app;
    this.appliedConfig = false;

    // Game data
    this.content = this.app?.data?.content || {};
    this.gameData = this.content.game || {};
    this.globalProperties = this.gameData.properties || {};
    this.qrcode = null;
    this.optionDescriptionTimeline = null;

    // Init rover instances
    this.rovers = [];
    $('.rover-preview').each((index, element) => {
      const uniqueId = $(element).attr('data-unique-id');
      const rover = new Rover(app, uniqueId, element);
      this.rovers.push(rover);
    });
    // Used to edit the rover instances
    this.roverUI = new RoverUI(app, this.rovers);

    // init
    this.init();
  }

  init() {
    // Click on stats to display a description
    $(document).on(this.app.touchEvent, ".property-list .property-item", (e) => {
      e.stopPropagation();

      const $item = $(e.currentTarget);
      if ($item.hasClass("is-active")) return;

      $(".property-list .property-item").removeClass("is-active");
      $item.addClass("is-active");
      this.updateStatsDescription($item.data("property"), $item.hasClass("warning-value"));
    });
  }

  /**
   * Updates the displayed rover stats and animates value changes or state colors.
   * @param {Object} newStats - The new stat values to display.
   * @param {Object} [oldStats={}] - The previous stat values for comparison.
   * @param {boolean} [skipTransition=false] - Whether to instantly update values without animations.
   */
  updateStats(newStats, oldStats = {}, skipTransition = false) {
    Object.entries(newStats).forEach(([key, rawValue]) => {
      // Check if property element exist
      const $item = $(`.property-list .property-item[data-property="${key}"]`);
      if (!$item.length) return;

      // Format value: booleans become "Yes" or "No", 1/2/3/4 become Short/Medium/Long/Extended, 999999 become "∞"
      let formattedValue;
      if (typeof rawValue === "boolean") formattedValue = (rawValue ? "Yes" : "No") // Boolean
      else if (rawValue >= 999999) formattedValue = "∞" // Infinite
      else if (rawValue !== "-" && key === "mission_length") formattedValue = ["-", "Short", "Medium", "Long", "Extended"][rawValue] // Mission length - Short / Medium / Long / Extended
      else if (rawValue !== "-" && key === "cargo_capacity") formattedValue = ["-", "Small", "Medium", "Large", "Extended"][rawValue] // Cargo capacity - Small / Medium / Large / Extended
      else if (rawValue !== undefined && rawValue !== null) formattedValue = String(rawValue); // String
      else formattedValue = this.globalProperties[key]?.default_value ?? "-";

      // Round numerical values
      if (!isNaN(formattedValue)) {
        const formattedFloat = parseFloat(formattedValue);
        // if higher than 999, remove decimal
        // if lower than 999, limit decimal to 2
        formattedValue = formattedFloat > 999 ? Math.round(formattedFloat) : Math.round(formattedFloat * 100) / 100;
      }

      // Build the en/fr display values.
      // Booleans get translated (Yes/No -> Oui/Non), everything else is shared as-is.
      const localizedValue = {
        "en": formattedValue,
        "fr": formattedValue === "Yes" ? "Oui" : formattedValue === "No" ? "Non" : formattedValue,
      }

      // Check for max weight limit warning (only for weight)
      // Nuclear fission power source + Weather sensor + Scoop and claw collectors
      const wasOverweight = (key === "weight" && $item.hasClass("warning-value"));
      const isOverweight = (key === "weight" && this.rovers[0].config.utilities.id === "A" && this.rovers[0].config.data_collection.id === "C" && this.rovers[0].config.sample_collection.id === "C")

      // Update up/middle/bottom class
      $item.removeClass("up-value middle-value bottom-value warning-value");
      const defaultValue = this.globalProperties[key]?.default_value ?? "-";

      // Remove warning text if we are coming from overweight state
      if (wasOverweight && !isOverweight) {
        this.updateStatsDescription($(".property-list .property-item.is-active").attr("data-property"), false);
      }

      // Show warning to tell user rover is too heavy
      if (isOverweight) {
        $item.addClass("warning-value");

        // Update property list and add warning message
        $(".property-list .property-item").removeClass("is-active");
        $(".property-list .property-item[data-property='weight']").addClass("is-active");
        this.updateStatsDescription('weight', true);

      } else if (formattedValue !== defaultValue) {
        const oldRaw = oldStats[key];

        if (typeof rawValue === "boolean") {

          // Boolean behavior
          if (oldRaw !== undefined && oldRaw !== defaultValue && typeof oldRaw === "boolean") {
            if (rawValue && !oldRaw) {
              $item.addClass("up-value");
            } else if (!rawValue && oldRaw) {
              $item.addClass("bottom-value");
            } else {
              // 2026-06-29 - Yellow state removed, as requested by Juliette. Stay white.
              // > Nous aimerions également retirer l'état jaune lorsque la valeur ne change pas
              // $item.addClass("middle-value");
            }
          }
        } else {

          // Numerical behavior
          const newValue = parseFloat(String(rawValue));
          const oldValue = parseFloat(String(oldRaw));
          let isUp = newValue > oldValue;
          let isDown = newValue < oldValue;

          if (isNaN(oldValue) || isUp) {
            $item.addClass("up-value");
          } else if (isDown) {
            $item.addClass("bottom-value");
          } else {
            // 2026-06-29 - Yellow state removed, as requested by Juliette. Stay white.
            // > Nous aimerions également retirer l'état jaune lorsque la valeur ne change pas
            // $item.addClass("middle-value");
          }
        }
      }

      // Skip if no value change
      if (oldStats[key] === rawValue) return;

      // Animate to new value
      const $value = $item.find(".value");
      const $en = $value.find(".en");
      const $fr = $value.find(".fr");

      if (skipTransition) {
        $en.text(localizedValue.en);
        $fr.text(localizedValue.fr);
        gsap.set($value, {opacity: 1.0});
        gsap.delayedCall(0.02, () => {
          $item.removeClass("no-transition");
        });
      } else {
        gsap.to($value, {
          opacity: 0,
          duration: 0.2,
          ease: "power1.out",
          onComplete: () => {
            $en.text(localizedValue.en);
            $fr.text(localizedValue.fr);
            gsap.to($value, {
              opacity: 1,
              duration: 0.2,
              ease: "power1.out",
            });
          }
        });
      }
    });
  }

  /**
   * When changing the rover options, display their description
   * @param {Object} content ex. { fr: { label: "...", text: "..." }, en: { label: "...", en: "..." } }
   */
  updateOptionDescription(content) {
    const $elements = this.getOptionDescriptionElements();

    // Kill the previous change so its content can't be written after this one
    this.optionDescriptionTimeline?.kill();

    this.optionDescriptionTimeline = gsap.timeline()
      .to(Object.values($elements), {
        opacity: 0,
        duration: 0.2,
        ease: "power1.out"
      })
      .add(() => {
        for (const [elementKey, $element] of Object.entries($elements)) {
          $element.each(function () {
            const lang = $(this).hasClass("fr") ? "fr" : "en";
            $(this).html(content[elementKey]?.[lang] || "");
          });
        }
      })
      .to(Object.values($elements), {
        opacity: 1,
        duration: 0.3,
        ease: "power1.out"
      });
  }

  /**
   * Label, description, pros and cons elements of the option description, keyed like the option content
   */
  getOptionDescriptionElements() {
    return {
      label: $(".description-preview .label").find(".fr, .en"),
      description: $(".description-preview .pastille-description").find(".fr, .en"),
      pros: $(".description-preview .pastille-pros").find(".fr, .en"),
      cons: $(".description-preview .pastille-cons").find(".fr, .en")
    };
  }

  /**
   * When pressing a statistic, display their description
   */
  updateStatsDescription(propertyKey, isOverweight = false) {
    let content = this.globalProperties[propertyKey]?.description;
    const $text = $(".property-description > .i18n").find(".fr, .en");

    // Overweight text override
    if (isOverweight) content = {...content, ...this.app.data.content.screen3.weight_warning};

    gsap.to($text, {
      opacity: 0,
      duration: 0.2,
      ease: "power1.out",
      onComplete: () => {
        $text.each(function () {
          const lang = $(this).hasClass("fr") ? "fr" : "en";
          $(this).html(content[lang] || "");
        });

        gsap.to($text, {
          opacity: 1,
          duration: 0.3,
          ease: "power1.out"
        });
      }
    });
  }

  /**
   * Resets the stats description to default state.
   */
  resetStatsDescription() {
    $(".property-list .property-item").removeClass("is-active");

    const $text = $(".property-description > .i18n").find(".fr, .en");
    gsap.killTweensOf($text);
    gsap.set($text, {opacity: 1});

    // Restore default description text
    const defaultDescription = this.content.screen3.description_default;
    $text.each(function () {
      const lang = $(this).hasClass("fr") ? "fr" : "en";
      $(this).html(defaultDescription[lang] || "");
    });
  }

  /**
   * Resets the option description to default state.
   */
  resetOptionDescription() {
    const {label: $label, description: $text, pros: $pros, cons: $cons} = this.getOptionDescriptionElements();

    // Kill any change in progress, including its pending content update
    this.optionDescriptionTimeline?.kill();
    this.optionDescriptionTimeline = null;
    gsap.set([$label, $text, $pros, $cons], {opacity: 1});

    // Restore default text
    [$label, $text].forEach(($el) => $el.html("..."));
    [$pros, $cons].forEach(($el) => $el.html(""));

    // Double requestAnimationFrame waits for the DOM to paint the reset state before re-enabling transitions
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        $(".description-preview").removeClass("no-transition");
      })
    })
  }

  updateStrengthAndTasks() {
    // Combine strengths and tasks as they are used the same way
    const $listItems = $(".listings-strengths li, .listings-tasks .task", "#screen-4");

    // Show/hide strength according to current rover configuration
    $listItems.each((index, el) => {
      const $strength = $(el);
      const condition = {
        optionType: $strength.data("condition-option-type"),
        optionValues: $strength.data("condition-option-values").split(',')
      };
      const conditionFulfilled = (condition.optionValues.includes(this.rovers[0].config[condition.optionType].id));
      $strength.toggle(conditionFulfilled)
    })
  }

  /**
   * Update the QR code src based on current rover configuration
   * Called when modifying the rover
   */
  updateQRCode() {
    const fullCode = this.getConfigCode();
    const shortCode = fullCode.split('').filter((char, index) => index % 2 === 0).join('');
    // Opens the share page next to the postcards (postcard_share_page/postcard.html)
    const url = `${this.app.data.postcards_url}/postcard.html?c=${shortCode}`;

    if (this.qrcode) {
      this.qrcode.clear();
      this.qrcode.makeCode(url);
    } else {
      // Build QR code
      this.qrcode = new QRCode($('#rover-qr-code').get(0), {
        text: url,
        width: 163,
        height: 163,
        colorDark: "#000000",
        colorLight: "#ffffff",
      });
    }
  }

  getConfigCode() {
    return this.rovers[0].getConfigCode();
  }

  /**
   * Generate all possible valid rover configuration codes based on available game data.
   * @returns {string[]} An array of all possible configuration code strings.
   */
  getAllConfigCodes(downloadFile = false, colors = null) {
    const roversData = this.gameData.rover || {};
    const cockpitIds = Object.keys(roversData);
    const colorIds = (colors || this.gameData.colors || []).map(c => String(c.id));
    const colorsToUse = colorIds.length > 0 ? colorIds : [this.roverUI.defaultColorId];

    // Helper to generate cartesian product of arrays
    const cartesian = (arrays) => arrays.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())), [[]]);

    // Gather all option IDs and color IDs available for each config key
    const optionsPerKey = this.rovers[0]?.configKeys.map(key => {
      const optionCombos = [];
      const isMandatory = (key === 'cockpit' || key === 'wheels');

      // For non-mandatory fields, allow an empty option id ('') which maps to '0' + default color
      if (!isMandatory) optionCombos.push({id: '', color: this.roverUI.defaultColorId});

      const ids = [];
      if (key === 'cockpit') {
        cockpitIds.forEach(id => {
          if (!ids.includes(id)) ids.push(id);
        });
      } else {
        Object.values(roversData).forEach(cockpit => {
          if (cockpit[key]) {
            Object.keys(cockpit[key]).forEach(optId => {
              if (!ids.includes(optId)) ids.push(optId);
            });
          }
        });
      }

      ids.forEach(optId => {
        if (isMandatory) {
          // Only add colors options for cockpit and wheels
          colorsToUse.forEach(colorId => {
            optionCombos.push({id: optId, color: colorId});
          });
        } else {
          // Else use default
          optionCombos.push({id: optId, color: this.roverUI.defaultColorId});
        }
      });
      return optionCombos;
    });

    // Generate full cartesian product combinations across all configuration keys
    const combinations = cartesian(optionsPerKey);
    const allCodes = [];

    combinations.forEach(combo => {
      let code = '';
      combo.forEach((item, index) => {
        const configKey = this.rovers[0]?.configKeys[index];
        const isMandatory = (configKey === 'cockpit' || configKey === 'wheels');

        // Mandatory fields (cockpit/wheels) can never have '0' as the option ID
        let finalOptId = item.id ? item.id : '0';
        if (isMandatory && finalOptId === '0') {
          // Fallback to the first available ID if somehow empty
          finalOptId = configKey === 'cockpit' ? (cockpitIds[0] || '1') : '1';
        }

        // Color can never be '0'; enforce fallback color
        const finalColorId = item.color ? item.color : this.roverUI.defaultColorId;
        const validColorId = finalColorId === '0' ? this.roverUI.defaultColorId : finalColorId;
        code += `${finalOptId}${validColorId}`;
      });
      allCodes.push(code);
    });

    // Create a Blob and trigger a browser download
    if (downloadFile) {
      const fileContent = allCodes.join('\n');
      const blob = new Blob([fileContent], {type: 'text/plain;charset=utf-8'});
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'rover-combinations.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    return allCodes;
  }

  reset(partial = false) {
    this.appliedConfig = false;

    // Reset stats
    $(".property-list .property-item").addClass("no-transition");
    gsap.set($(".property-list .property-item .border-shape"), {opacity: 0.0});
    $(".property-list .property-item").removeClass("is-active up-value middle-value bottom-value warning-value");

    // Reset stats/options description
    $(".description-preview").addClass("no-transition");
    this.resetStatsDescription();
    this.resetOptionDescription();


    // Reset the rover to default state
    this.roverUI.reset(partial);
    this.rovers.forEach((rover, index) => {
      rover.reset();

      // Reset stats to default state
      if (index === 0) {
        this.updateStats(rover.currentStats, {}, true);
      }
    });
  }
}
